(() => {
  if (window.ppCollectionMore) return;
  window.ppCollectionMore = true;
  const states = new WeakMap();
  const sectionFor = node => node?.closest('[data-section-id]');
  const keyFor = card => card.dataset.family || card.dataset.productId;
  const available = card => card.dataset.ppAvailable !== 'false' || card.dataset.ppStarter === 'true';
  const cardsIn = grid => [...grid.querySelectorAll(':scope > [data-pp-card]')];
  const text = (state, key) => state.pagination.dataset[key] || '';

  // Partition the entire paginated result, not just the currently visible page.
  // Sold-out families stay buffered until every page of purchasable items has been read.
  function ingest(state, cards) {
    const added = [];
    for (const card of cards) {
      const key = keyFor(card);
      if (state.seen.has(key)) { card.remove(); continue; }
      if (available(card)) {
        state.soldOut.delete(key);
        state.seen.add(key);
        state.grid.append(card);
        added.push(card);
      } else {
        if (!state.soldOut.has(key)) state.soldOut.set(key, card);
        card.remove();
      }
    }
    return added;
  }
  function flushSoldOut(state, limit = 24) {
    const added = [];
    for (const [key, card] of state.soldOut) {
      state.soldOut.delete(key);
      if (!state.seen.has(key)) {
        state.seen.add(key); state.grid.append(card); added.push(card);
      }
      if (added.length >= limit) break;
    }
    return added;
  }
  function update(state) {
    const more = Boolean(state.next || state.soldOut.size);
    let link = state.pagination.querySelector('[data-pp-load-more]');
    if (more && !link) {
      link = document.createElement('a'); link.className = 'pph-button';
      link.dataset.ppLoadMore = ''; link.textContent = text(state, 'more');
      state.pagination.prepend(link);
    }
    if (link) {
      if (more) {
        link.href = state.next || state.fallback;
        link.removeAttribute('aria-disabled');
      } else link.remove();
    }
    state.status.textContent = '';
    state.pagination.hidden = !more;
    state.pagination.removeAttribute('aria-busy');
  }
  function observe(section, state) {
    state.observer?.disconnect();
    if (!state.next && !state.soldOut.size || state.paused || !('IntersectionObserver' in window)) return;
    state.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting) && section.getAttribute('aria-busy') !== 'true') load(section, false);
    }, {rootMargin: '0px 0px 550px 0px'});
    state.observer.observe(state.pagination);
  }
  function stop(section) {
    const state = states.get(section);
    state?.observer?.disconnect(); state?.controller?.abort(); states.delete(section);
  }
  function initialiseSection(section) {
    stop(section);
    // Separate series/bestseller blocks are finite lists, so partition them in place.
    section.querySelectorAll('.product-series-block:not(#series-overige) > ul').forEach(grid => {
      cardsIn(grid).filter(card => !available(card)).forEach(card => grid.append(card));
    });
    const grid = section.querySelector('[data-pp-collection-grid]');
    const pagination = section.querySelector('[data-pp-pagination]');
    if (!grid || !pagination) return;
    const state = {grid, pagination, status: pagination.querySelector('[data-pp-page-status]'),
      next: pagination.querySelector('[data-pp-load-more]')?.getAttribute('href') || null,
      fallback: location.href, seen: new Set(), soldOut: new Map(), controller: null, observer: null, paused: false};
    states.set(section, state);
    ingest(state, cardsIn(grid));
    if (!state.next) flushSoldOut(state);
    update(state);
    document.dispatchEvent(new CustomEvent('pp:content:updated', {detail: {root: grid}}));
    // Fill a useful first viewport even when Shopify's first page is mostly sold out.
    if (state.next && cardsIn(grid).length < 12) load(section, false);
    else observe(section, state);
  }
  async function load(section, manual) {
    const state = states.get(section);
    if (!state || state.controller || !state.grid.isConnected) return;
    state.observer?.disconnect();
    const controller = new AbortController(); state.controller = controller;
    state.pagination.setAttribute('aria-busy', 'true');
    state.pagination.querySelector('[data-pp-load-more]')?.setAttribute('aria-disabled', 'true');
    state.status.textContent = text(state, 'loading');
    const added = [];
    try {
      // Continue across empty/deduplicated pages. Commit each successful page, so
      // retries never lose buffered products or request an already consumed page.
      while (state.next && added.length < 24) {
        const url = new URL(state.next, location.origin);
        if (url.origin !== location.origin) throw Error('origin');
        url.searchParams.set('section_id', section.dataset.sectionId);
        const timeout = setTimeout(() => controller.abort(), 20000);
        let response;
        try {
          response = await fetch(url, {credentials: 'same-origin', signal: controller.signal});
          if (!response.ok) throw Error('load');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = doc.querySelector('[data-pp-collection-grid]');
          if (!source || !state.grid.isConnected || controller.signal.aborted || states.get(section) !== state) throw Error('load');
          const next = doc.querySelector('[data-pp-load-more]')?.getAttribute('href') || null;
          if (next && new URL(next, location.origin).href === new URL(state.next, location.origin).href) throw Error('pagination');
          added.push(...ingest(state, cardsIn(source)));
          state.next = next;
          state.status.textContent = text(state, 'loading');
        } finally { clearTimeout(timeout); }
      }
      if (!state.next && added.length < 24) added.push(...flushSoldOut(state, 24 - added.length));
      state.paused = false; update(state);
      if (manual && added[0]) added[0].querySelector('.pp-product-card__title a')?.focus({preventScroll: true});
    } catch (error) {
      if (states.get(section) === state && state.grid.isConnected) {
        state.paused = true; update(state); state.status.textContent = text(state, 'error');
      }
    } finally {
      state.controller = null;
      if (states.get(section) === state && state.grid.isConnected) {
        document.dispatchEvent(new CustomEvent('pp:content:updated', {detail: {root: state.grid}}));
        observe(section, state);
      }
    }
  }
  function initialise(root = document) {
    root.querySelectorAll('[data-pp-stock-order][data-section-id]').forEach(initialiseSection);
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-pp-load-more]');
    if (!link || event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const section = sectionFor(link);
    if (!states.has(section)) return;
    event.preventDefault(); load(section, true);
  });
  document.addEventListener('pp:collection:loading', event => stop(event.detail.root));
  document.addEventListener('pp:collection:updated', event => initialiseSection(event.detail.root));
  window.addEventListener('collectionSort', () => {
    document.querySelectorAll('.collection-wrapper[data-pp-stock-order]').forEach(section => {
      if (states.get(section)?.grid !== section.querySelector('[data-pp-collection-grid]')) initialiseSection(section);
    });
  });
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-section-id]').forEach(stop));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(), {once: true});
  else initialise();
})();
